/**
 * Calendar sub-tab — month grid of scheduled messages (M1.14 wave 2).
 *
 * The grid covers the visible month plus the leading/trailing days that pad
 * to a full Mon-first 6-week layout (always 42 cells). Each cell shows up
 * to 6 message previews; overflow becomes a "+N more" line so dense days
 * stay readable.
 *
 * Clicking a blank cell opens the compose modal with that date pre-seeded
 * (10am local). Clicking an existing message opens the modal in edit mode
 * — the stopPropagation on the inner button keeps the blank-cell handler
 * from also firing.
 *
 * The grid query fetches a month either side of the current view so
 * navigating prev/next is instant without a refetch.
 */
import type {
  CommunicationChannelView,
  CommunicationScheduledMessageView,
} from '@peerless/shared';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { apiRequest } from '../../lib/api-client';
import { cn } from '../../lib/cn';
import { queryKeys } from '../../lib/query-keys';

import { ComposeModal } from './ComposeModal';


interface MonthCell {
  date: Date;
  inMonth: boolean;
  isToday: boolean;
  items: CommunicationScheduledMessageView[];
}

export function CalendarPane(): JSX.Element {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [composeFor, setComposeFor] = useState<{
    editing?: CommunicationScheduledMessageView | null;
    defaultDate?: Date | null;
  } | null>(null);

  const { from, to } = useMemo(() => {
    const start = new Date(month.getFullYear(), month.getMonth() - 1, 1);
    const end = new Date(month.getFullYear(), month.getMonth() + 2, 0, 23, 59, 59);
    return { from: start.toISOString(), to: end.toISOString() };
  }, [month]);

  const channelsQuery = useQuery({
    queryKey: queryKeys.communication.channels(),
    queryFn: () => apiRequest<CommunicationChannelView[]>('/communication/channels'),
  });
  const scheduledQuery = useQuery({
    queryKey: queryKeys.communication.scheduled({ from, to }),
    queryFn: () =>
      apiRequest<CommunicationScheduledMessageView[]>(
        `/communication/scheduled-messages?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
  });

  const channels = channelsQuery.data ?? [];
  const scheduled = useMemo(() => scheduledQuery.data ?? [], [scheduledQuery.data]);

  const monthLabel = month.toLocaleString([], { month: 'long', year: 'numeric' });
  const grid = useMemo(() => buildMonthGrid(month, scheduled), [month, scheduled]);

  return (
    <div className="flex h-full min-h-0 flex-col px-6 py-5">
      <header className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-xs uppercase tracking-tight text-muted">
            Calendar
          </p>
          <h2 className="mt-1 text-xl font-semibold tracking-tight text-text">
            {monthLabel}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() =>
              setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))
            }
            aria-label="Previous month"
            className="grid size-8 place-items-center rounded-sm border border-border bg-surface text-muted hover:bg-bg hover:text-text"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            onClick={() =>
              setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))
            }
            aria-label="Next month"
            className="grid size-8 place-items-center rounded-sm border border-border bg-surface text-muted hover:bg-bg hover:text-text"
          >
            <ChevronRight className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => setComposeFor({ editing: null, defaultDate: null })}
            disabled={channels.length === 0}
            className="inline-flex items-center gap-1.5 rounded-sm bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg hover:opacity-90 disabled:opacity-50"
          >
            <Plus className="size-3.5" />
            Compose
          </button>
        </div>
      </header>

      {channels.length === 0 && (
        <p className="rounded-sm border border-dashed border-border bg-bg/30 px-3 py-3 text-xs text-muted">
          No channels granted. Ask an admin to grant you access on a channel before
          composing.
        </p>
      )}

      {/*
       * Grid layout:
       *   row 1     — day-name header (auto-sized)
       *   rows 2-7  — 6 week rows, each 1fr so they share remaining height
       *
       * Combined with `flex-1 min-h-0` on the outer wrapper this makes the
       * calendar fill the full Calendar sub-tab regardless of viewport.
       */}
      <div
        className="mt-3 grid flex-1 min-h-0 grid-cols-7 gap-px overflow-hidden rounded border border-border bg-border text-xs"
        style={{ gridTemplateRows: 'auto repeat(6, minmax(0, 1fr))' }}
      >
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div
            key={d}
            className="bg-bg/30 px-2 py-1.5 text-center font-mono text-[10px] uppercase tracking-tight text-muted"
          >
            {d}
          </div>
        ))}
        {grid.map((cell, i) => (
          <button
            type="button"
            key={i}
            onClick={() =>
              channels.length > 0 &&
              setComposeFor({ editing: null, defaultDate: cell.date })
            }
            className={cn(
              'group flex min-h-0 flex-col overflow-hidden bg-surface px-1.5 py-1 text-left transition hover:bg-bg',
              !cell.inMonth && 'bg-bg/40 text-muted',
            )}
          >
            <div className="flex items-center justify-between">
              <span
                className={cn(
                  'inline-flex size-5 items-center justify-center rounded-full text-[10px] font-medium',
                  cell.isToday ? 'bg-accent text-accent-fg' : 'text-muted',
                )}
              >
                {cell.date.getDate()}
              </span>
            </div>
            <ul className="mt-1 flex-1 space-y-0.5 overflow-y-auto">
              {cell.items.slice(0, 6).map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setComposeFor({ editing: item, defaultDate: null });
                    }}
                    className="block w-full truncate rounded-sm bg-accent/10 px-1 py-0.5 text-left text-[10px] text-text hover:bg-accent/20"
                  >
                    <span className="font-mono text-muted">
                      {item.scheduledAt
                        ? new Date(item.scheduledAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : 'now'}
                    </span>{' '}
                    · {item.emailSubject ?? item.body.slice(0, 40)}
                  </button>
                </li>
              ))}
              {cell.items.length > 6 && (
                <li className="px-1 text-[10px] text-muted">
                  +{cell.items.length - 6} more
                </li>
              )}
            </ul>
          </button>
        ))}
      </div>

      {composeFor && (
        <ComposeModal
          channels={channels}
          editing={composeFor.editing ?? null}
          defaultDate={composeFor.defaultDate ?? null}
          onClose={() => setComposeFor(null)}
        />
      )}
    </div>
  );
}

function buildMonthGrid(
  month: Date,
  scheduled: CommunicationScheduledMessageView[],
): MonthCell[] {
  // Mon-first grid covering the visible month.
  const firstOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
  const dayOfWeek = (firstOfMonth.getDay() + 6) % 7; // Mon=0
  const gridStart = new Date(firstOfMonth);
  gridStart.setDate(gridStart.getDate() - dayOfWeek);

  const byDay = new Map<string, CommunicationScheduledMessageView[]>();
  for (const m of scheduled) {
    const stamp = m.scheduledAt
      ? new Date(m.scheduledAt)
      : new Date(m.createdAt);
    const key = stampKey(stamp);
    const list = byDay.get(key) ?? [];
    list.push(m);
    byDay.set(key, list);
  }

  const today = new Date();
  const cells: MonthCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push({
      date: d,
      inMonth: d.getMonth() === month.getMonth(),
      isToday: stampKey(d) === stampKey(today),
      items: byDay.get(stampKey(d)) ?? [],
    });
  }
  return cells;
}

function stampKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
